import { EventEmitter, inject, Injectable } from '@angular/core';
import { Subject, takeUntil, throttleTime } from 'rxjs';
import type { ZegoRoomStateChangedReason } from 'zego-express-engine-webrtc/sdk/code/zh/ZegoExpressEntity.rtm';
import {
  ZegoPublisherState,
  ZegoSoundLevelInfo,
} from 'zego-express-engine-webrtc/sdk/code/zh/ZegoExpressEntity.web';
import { QualityGrade } from 'zego-express-engine-webrtc/sdk/src/common/zego.entity.web';

import { CallZegoService } from './call-zego.service';

export interface CallZegoEventHandlers {
  onRemoteMicStatusUpdate: (streamID: string, status: 'OPEN' | 'MUTE') => void;
  onRemoteCameraStatusUpdate: (streamID: string, status: 'OPEN' | 'MUTE') => void;
  onSoundLevelUpdate: (soundLevelList: ZegoSoundLevelInfo[]) => void;
  onNetworkQuality: (userID: string, upstreamQuality: number, downstreamQuality: number) => void;
  onRoomStateChanged: (
    roomID: string,
    reason: ZegoRoomStateChangedReason,
    errorCode: number,
    extendedData: string,
  ) => void;
  onPublisherStateUpdate: (result: ZegoPublisherState) => void;
}

@Injectable({
  providedIn: 'root',
})
export class CallZegoListenerService {
  private callZegoService = inject(CallZegoService);

  private ngUnsubscribe = new Subject<void>();

  register(handlers: CallZegoEventHandlers): void {
    const engine = this.callZegoService.getEngine();

    const soundLevelEventEmitter = new EventEmitter<ZegoSoundLevelInfo[]>();
    soundLevelEventEmitter
      .asObservable()
      .pipe(throttleTime(1000), takeUntil(this.ngUnsubscribe))
      .subscribe((soundLevelList: ZegoSoundLevelInfo[]) => {
        if (!soundLevelList || soundLevelList.length == 0) {
          return;
        }

        handlers.onSoundLevelUpdate(soundLevelList);
      });

    const onSoundLevelUpdate = (soundLevelList: ZegoSoundLevelInfo[]) => {
      soundLevelEventEmitter.emit(soundLevelList);
    };

    const networkQualityEventEmitter = new EventEmitter<{
      userID: string;
      upstreamQuality: QualityGrade;
      downstreamQuality: QualityGrade;
    }>();
    networkQualityEventEmitter
      .asObservable()
      .pipe(throttleTime(10000), takeUntil(this.ngUnsubscribe))
      .subscribe(({ userID, upstreamQuality, downstreamQuality }) => {
        handlers.onNetworkQuality(userID, upstreamQuality, downstreamQuality);
      });

    const onNetworkQuality = (
      userID: string,
      upstreamQuality: QualityGrade,
      downstreamQuality: QualityGrade,
    ) => {
      networkQualityEventEmitter.emit({ userID, upstreamQuality, downstreamQuality });
    };

    const onRemoteMicStatusUpdate = handlers.onRemoteMicStatusUpdate;
    const onRemoteCameraStatusUpdate = handlers.onRemoteCameraStatusUpdate;
    const onRoomStateChanged = handlers.onRoomStateChanged;
    const onPublisherStateUpdate = handlers.onPublisherStateUpdate;

    engine.on('remoteMicStatusUpdate', onRemoteMicStatusUpdate);
    engine.on('remoteCameraStatusUpdate', onRemoteCameraStatusUpdate);
    engine.on('soundLevelUpdate', onSoundLevelUpdate);
    engine.on('networkQuality', onNetworkQuality);
    engine.on('roomStateChanged', onRoomStateChanged);
    engine.on('publisherStateUpdate', onPublisherStateUpdate);
  }

  clearCallback() {
    const engine = this.callZegoService.getEngine();
    engine.off('remoteMicStatusUpdate');
    engine.off('remoteCameraStatusUpdate');
    engine.off('soundLevelUpdate');
    engine.off('networkQuality');
    engine.off('roomStateChanged');
    engine.off('publisherStateUpdate');

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
